export interface RemoteError {
  code: number
  responseData: unknown
}

export interface TelErrorJson {
  type: string
  code: number
  domainType?: string
  message: string
  stack?: string
  validationErrors?: unknown[]
  errorData?: unknown // when server errors don't have a consistent format store all data here
}

export class SerializableError extends Error {
  serializable = true
  toJSON(): TelErrorJson {
    return {
      type: this.name,
      code: 500,
      message: 'Should not have been called. please override me',
    }
  }
}

// class CustomError extends SerializableError implements TelError { constructor(data: RemoteError) { ... } }
export interface TelError {
  // baggage?: Baggage // probably is not needed: if error is thrown in startSpan it will be associated with it, oterwise use span.end({ error })
  serializable: boolean // it's safe to call toJSON() on such errors
  toJSON(): TelErrorJson
}

interface ErrorDetails {
  spanId?: string
  traceId?: string // for exporting to sentry
  requestId?: string

  spanName?: string // eg endpoint name
  spanType?: string

  type: 'unknown' | 'server' | 'internal' | 'client' | (string & {})
  name: string
  message: string
  stack?: string
  cause?: unknown // it can store TelError if the cause is another error, or serialized error object
  validationErrors?: unknown[] // since server might not be instrumented we should duplicate data on client
  level?: 'warning' | 'error' // 4xx errors are warnings
}

// about type:
// unknown - no cause
// internal - error in our code
// client - 4xx
// server - on the client we mark error as server if it's cause is a server error
// other - custom error

export interface TelServerErrorData extends ErrorDetails {
  origin: 'server'
  status: number
  causeStatusCode?: number // eg 3rd party responses with 4xx but our server returns 5xx
}

export interface TelClientErrorData extends ErrorDetails {
  origin: 'client'
  type: 'unknown' | 'server' | (string & {}) // mirror server type to certain extent
  duplicated?: boolean // if type: server error is duplicated and should not be tracked separately
  serverStatus?: number // 403 and 404 are not errors
  componentStack?: string
}

export type TelErrorData = TelServerErrorData | TelClientErrorData

export const telErrorDataToTags = (data: TelErrorData) => cleanObject(telErrorDataToTags_(data))

const telErrorDataToTags_ = (data: TelErrorData) => {
  if (data.origin === 'server') {
    return {
      type: data.type,
      name: data.name,
      spanName: data.spanName,
      spanType: data.spanType,
    }
  }

  return {
    type: data.type,
    status: data.serverStatus,
    spanName: data.spanName,
    spanType: data.spanType,
  }
}

function cleanObject<T>(obj: T): T {
  if (Array.isArray(obj)) {
    // Recursively clean each item in the array
    return obj.map((item) => cleanObject(item)).filter((item) => item !== undefined) as unknown as T
  }
  if (obj !== null && typeof obj === 'object') {
    // Recursively clean each property in the object
    // biome-ignore lint/suspicious/noExplicitAny: <explanation>
    const result: any = {}
    Object.entries(obj).forEach(([key, value]) => {
      if (value === '' || value === undefined) {
        // Skip keys with '' or undefined
        return
      }
      const cleanedValue = cleanObject(value)
      if (
        cleanedValue !== undefined &&
        (typeof cleanedValue !== 'object' ||
          (cleanedValue !== null &&
            (Array.isArray(cleanedValue)
              ? cleanedValue.length > 0
              : Object.keys(cleanedValue).length > 0)))
      ) {
        result[key] = cleanedValue
      }
    })
    return result as T
  }
  // Return the value if it's not an object or array
  return obj
}
